/**
 * prescreenFormBridge — lets the prescreen question editor look and work like
 * the form builder's (type tiles, "Mark as NOT ELIGIBLE if answer…") while the
 * prescreen keeps its own storage, which the phone runner, the verdicts, the
 * pivot engine and the texting agent all read:
 *
 *   - answer types: boolean | choice | number | height | date | text, plus a
 *     `calculation` block — shown as the form tiles Yes / No, Single Choice,
 *     Number, Height, Date, Short Text and Calculation;
 *   - `rule` = the PASSING condition (eq/neq/gt/gte/lt/lte/between/in/exists,
 *     AND/OR groups) — shown as the form builder's single EXCLUDE rule. Every
 *     exclude rule the editor offers has an exact passing complement
 *     (excludeToRule); a stored rule with no single-exclude equivalent (an
 *     AI-drafted "> 18", an `exists`, a three-way group) is reported as not
 *     exact by ruleToExclude and the editor keeps it untouched.
 *
 * The prescreen has no "needs review" outcome (verdicts are pass / fail /
 * recruiter judgment), so there is no flag-for-review rule here.
 */
import { DraftCriterion } from '../../types';
import { OPERATOR_LABELS, eligibilityOperatorsForType } from '../forms/formMeta';

/** One sibling criterion a calculation input or show_if condition may reference. */
export interface CriterionSibling {
  ref?: string;
  display_question: string;
  answer_type: string;
  options?: { value: string; label: string }[] | null;
  calculation?: DraftCriterion['calculation'];
}

export type ExcludeRule = { operator: string; value: any };
type Option = { value: string; label: string };

/** The form type tiles a prescreen question can be, in picker order. */
export const PRESCREEN_FORM_TYPES = ['text', 'number', 'height', 'date', 'single_choice', 'yes_no', 'calculation'];

/** Prescreen answer type (+ calculation) → the form type tile it shows as. */
export function formTypeOf(c: { answer_type: string; calculation?: unknown }): string {
  if (c.calculation) return 'calculation';
  switch (c.answer_type) {
    case 'boolean': return 'yes_no';
    case 'choice': return 'single_choice';
    case 'number':
    case 'height':
    case 'date':
    case 'text':
      return c.answer_type;
    default: return 'text';
  }
}

/** Form type tile → the prescreen answer_type it stores (a calculation is numeric). */
export function answerTypeOf(formType: string): string {
  switch (formType) {
    case 'yes_no': return 'boolean';
    case 'single_choice': return 'choice';
    case 'calculation': return 'number';
    case 'number':
    case 'height':
    case 'date':
      return formType;
    default: return 'text';
  }
}

const isNumericType = (formType: string) =>
  formType === 'number' || formType === 'height' || formType === 'calculation';

/** The type the shared rule builder should render values for (height is total inches). */
export const ruleQuestionType = (formType: string) => (formType === 'height' ? 'number' : formType);

/** The "not eligible if" operators offered: the form builder's menu, minus what
 *  the prescreen engine cannot evaluate (`contains`), plus "is one of" on a
 *  single choice (the passing set is the other options). */
export function prescreenExcludeOperators(formType: string): string[] {
  if (isNumericType(formType)) return eligibilityOperatorsForType('number');
  if (formType === 'single_choice') return ['equals', 'not_equals', 'in'];
  return ['equals', 'not_equals'];
}

const toNum = (v: any): number => (v === '' || v === null || v === undefined ? NaN : Number(v));
const blank = (v: any) => v === '' || v === null || v === undefined;

/** Exclude rule → the prescreen passing rule, or an error naming the fix. */
export function excludeToRule(
  ex: ExcludeRule | null,
  formType: string,
  options: Option[],
): { rule: any } | { error: string } {
  if (!ex) return { rule: {} };
  const incomplete = { error: 'Finish the "not eligible" rule (fill in its value), or remove it.' };
  const numeric = isNumericType(formType);
  switch (ex.operator) {
    case 'equals':
    case 'not_equals': {
      if (blank(ex.value)) return incomplete;
      const excludeWhenEqual = ex.operator === 'equals';
      if (formType === 'yes_no') {
        const yes = String(ex.value) === 'yes';
        // Excluded on "Yes" → passes on No (eq false); excluded unless "Yes" → passes on Yes.
        return { rule: { op: 'eq', value: excludeWhenEqual ? !yes : yes } };
      }
      let value: any = String(ex.value);
      if (numeric) {
        value = toNum(ex.value);
        if (!Number.isFinite(value)) return incomplete;
      }
      return { rule: { op: excludeWhenEqual ? 'neq' : 'eq', value } };
    }
    case 'greater_than':
    case 'less_than': {
      const n = toNum(ex.value);
      if (!Number.isFinite(n)) return incomplete;
      return { rule: { op: ex.operator === 'greater_than' ? 'lte' : 'gte', value: n } };
    }
    case 'between':
    case 'not_between': {
      const range = Array.isArray(ex.value) ? ex.value : [];
      const a = toNum(range[0]);
      const b = toNum(range[1]);
      if (!Number.isFinite(a) || !Number.isFinite(b)) return incomplete;
      if (a > b) return { error: 'In the "not eligible" rule, the first number must not be larger than the second.' };
      if (ex.operator === 'not_between') return { rule: { op: 'between', min: a, max: b } };
      return { rule: { combinator: 'or', conditions: [{ op: 'lt', value: a }, { op: 'gt', value: b }] } };
    }
    case 'in': {
      const excluded = new Set((Array.isArray(ex.value) ? ex.value : []).map(String));
      if (excluded.size === 0) return { error: 'Pick at least one answer for the "not eligible" rule, or remove it.' };
      const passing = options.map((o) => o.value).filter((v) => !excluded.has(v));
      if (passing.length === 0) return { error: 'That rule makes every answer not eligible. Leave at least one answer eligible.' };
      return { rule: { op: 'in', values: passing } };
    }
    default:
      return incomplete;
  }
}

const isEmptyRule = (rule: any) =>
  !rule || typeof rule !== 'object' || (!rule.op && !(rule.combinator && Array.isArray(rule.conditions) && rule.conditions.length > 0));

const toBool = (v: any): boolean | undefined => {
  if (v === true || v === 'true' || v === 'yes') return true;
  if (v === false || v === 'false' || v === 'no') return false;
  return undefined;
};

/**
 * Stored prescreen passing rule → the single exclude rule the editor shows.
 * `exact: false` means the rule has no single-exclude equivalent; the editor
 * must keep it as-is rather than show (and save) an approximation.
 */
export function ruleToExclude(
  rule: any,
  formType: string,
  options: Option[],
): { exclude: ExcludeRule | null; exact: boolean } {
  const no = { exclude: null, exact: false };
  if (isEmptyRule(rule)) return { exclude: null, exact: true };
  const numeric = isNumericType(formType);
  const optionValues = options.map((o) => o.value);

  if (rule.combinator && Array.isArray(rule.conditions)) {
    if (!numeric || rule.conditions.length !== 2) return no;
    const [x, y] = rule.conditions;
    const find = (op: string) => [x, y].find((c) => c && c.op === op && typeof c.value === 'number');
    if (rule.combinator === 'or') {
      const lo = find('lt');
      const hi = find('gt');
      if (lo && hi && lo !== hi && lo.value <= hi.value) {
        return { exclude: { operator: 'between', value: [String(lo.value), String(hi.value)] }, exact: true };
      }
    } else if (rule.combinator === 'and') {
      const lo = find('gte');
      const hi = find('lte');
      if (lo && hi && lo !== hi && lo.value <= hi.value) {
        return { exclude: { operator: 'not_between', value: [String(lo.value), String(hi.value)] }, exact: true };
      }
    }
    return no;
  }

  switch (rule.op) {
    case 'eq':
    case 'neq': {
      if (formType === 'yes_no') {
        const b = toBool(rule.value);
        if (b === undefined) return no;
        const passesOnYes = rule.op === 'eq' ? b : !b;
        return { exclude: { operator: 'equals', value: passesOnYes ? 'no' : 'yes' }, exact: true };
      }
      if (rule.value === undefined || rule.value === null) return no;
      if (formType === 'single_choice' && !optionValues.includes(String(rule.value))) return no;
      if (numeric && !Number.isFinite(toNum(rule.value))) return no;
      return { exclude: { operator: rule.op === 'eq' ? 'not_equals' : 'equals', value: String(rule.value) }, exact: true };
    }
    case 'gte':
    case 'lte':
      if (!numeric || typeof rule.value !== 'number') return no;
      return { exclude: { operator: rule.op === 'gte' ? 'less_than' : 'greater_than', value: String(rule.value) }, exact: true };
    case 'between':
      if (!numeric || typeof rule.min !== 'number' || typeof rule.max !== 'number') return no;
      return { exclude: { operator: 'not_between', value: [String(rule.min), String(rule.max)] }, exact: true };
    case 'in': {
      if (formType !== 'single_choice' || !Array.isArray(rule.values)) return no;
      const passing = rule.values.map(String);
      if (passing.some((v: string) => !optionValues.includes(v))) return no;
      const excluded = optionValues.filter((v) => !passing.includes(v));
      if (excluded.length === 0) return no;
      if (excluded.length === 1) return { exclude: { operator: 'equals', value: excluded[0] }, exact: true };
      if (passing.length === 1) return { exclude: { operator: 'not_equals', value: passing[0] }, exact: true };
      return { exclude: { operator: 'in', value: excluded }, exact: true };
    }
    default:
      return no; // gt / lt / exists: no single-exclude equivalent
  }
}

/** "Not eligible if answer is greater than 65" for the collapsed card, or null
 *  when the rule has no exclude form (the caller falls back to "Eligible if …"). */
export function excludeSummary(rule: any, formType: string, options: Option[]): string | null {
  const { exclude, exact } = ruleToExclude(rule, formType, options);
  if (!exact || !exclude) return null;
  const labelOf = (v: any) => options.find((o) => o.value === String(v))?.label || String(v);
  const v = exclude.value;
  let text: string;
  if (exclude.operator === 'between' || exclude.operator === 'not_between') text = `${v[0]} and ${v[1]}`;
  else if (exclude.operator === 'in') text = (v as any[]).map(labelOf).join(', ');
  else if (formType === 'yes_no') text = v === 'yes' ? 'Yes' : 'No';
  else if (formType === 'single_choice') text = labelOf(v);
  else text = String(v);
  return `Not eligible if answer ${OPERATOR_LABELS[exclude.operator] || exclude.operator} ${text}`;
}
